import { Logo } from "@components/brand/Logo";
import { useStoreLink } from "@components/shared/StoreBadges";
import { Locale } from "@services/strings";
import { cn } from "@/ui/utils/cn";
import { track } from "@services/analytics";
import { useTranslations } from "@services/strings";
import { Menu, X } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

const NAV = [
  { href: "/unlock", key: "site.nav.unlock" },
  { href: "/pay-less", key: "site.nav.payLess" },
  { href: "/mortgage", key: "site.nav.mortgage" },
  { href: "/auto-loans", key: "site.nav.autoLoans" },
  { href: "/help", key: "site.nav.help" },
] as const;

const useSetLocale = () => {
  const router = useRouter();

  return (locale: Locale) => {
    const { pathname, asPath, query } = router;
    document.cookie = `NEXT_LOCALE=${locale}; max-age=${60 * 60 * 24 * 365}; path=/`;
    router.push({ pathname, query }, asPath, { locale });
  };
};

const LanguageToggle = ({ className }: { className?: string }) => {
  const router = useRouter();
  const { t } = useTranslations();
  const setLocale = useSetLocale();
  const isArabic = router.locale === "ar-EG";

  return (
    <button
      type="button"
      onClick={() => setLocale(isArabic ? "en-EG" : "ar-EG")}
      className={cn(
        "rounded-full border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-400 hover:text-ink",
        className,
      )}
      aria-label={t("site.nav.language")}
      // Always render the *other* language's label in its own script.
      lang={isArabic ? "en" : "ar"}
      dir={isArabic ? "ltr" : "rtl"}
    >
      {isArabic ? t("site.nav.english") : t("site.nav.arabic")}
    </button>
  );
};

/**
 * Sticky site header: wordmark, product nav, language toggle and the app CTA.
 * On phones the nav folds into a panel under the bar.
 */
export const SiteHeader = ({ tone = "white" }: { tone?: "white" | "navy" }) => {
  const { t } = useTranslations();
  const router = useRouter();
  const storeLink = useStoreLink();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    router.events.on("routeChangeStart", close);
    return () => router.events.off("routeChangeStart", close);
  }, [router.events]);

  const invert = tone === "navy";
  const isActive = (href: string) => router.pathname.startsWith(href);

  return (
    <header
      className={cn(
        "sticky top-0 z-30 border-b backdrop-blur",
        invert
          ? "border-white/10 bg-navy/90 text-white"
          : "border-slate-100 bg-white/90 text-ink",
      )}
    >
      <div className="container flex h-16 items-center justify-between gap-6 md:h-[72px]">
        <Link
          href="/"
          className="flex shrink-0 items-center"
          aria-label={t("site.common.backHome")}
        >
          <Logo height={28} />
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "rounded-full px-3.5 py-2 text-[15px] font-medium transition-colors",
                invert
                  ? "text-slate-200 hover:bg-white/10 hover:text-white"
                  : "text-slate-600 hover:bg-mist hover:text-ink",
                isActive(item.href) &&
                  (invert ? "bg-white/10 text-white" : "bg-mist text-ink"),
              )}
              aria-current={isActive(item.href) ? "page" : undefined}
            >
              {t(item.key)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <LanguageToggle
            className={cn(
              invert &&
                "border-white/20 text-white hover:border-white/50 hover:text-white",
            )}
          />
          <a
            href={storeLink}
            onClick={() =>
              track({
                event: "Button Clicked",
                properties: { label: "Header - Get the app" },
              })
            }
            className={cn(
              "hidden h-10 items-center rounded-full px-5 text-sm font-semibold transition-colors sm:inline-flex",
              invert
                ? "bg-mint text-ink hover:bg-mint-soft"
                : "bg-ink text-white hover:bg-navy",
            )}
          >
            {t("site.common.getApp")}
          </a>
          <button
            type="button"
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-full md:hidden",
              invert ? "hover:bg-white/10" : "hover:bg-mist",
            )}
            aria-expanded={open}
            aria-controls="site-menu"
            aria-label={open ? t("site.nav.close") : t("site.nav.menu")}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <div
        id="site-menu"
        hidden={!open}
        className={cn(
          "border-t md:hidden",
          invert ? "border-white/10 bg-navy" : "border-slate-100 bg-white",
        )}
      >
        <nav className="container flex flex-col py-3" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "rounded-xl px-3 py-3 text-base font-medium",
                invert
                  ? "text-slate-100 hover:bg-white/10"
                  : "text-ink hover:bg-mist",
              )}
            >
              {t(item.key)}
            </Link>
          ))}
          <a
            href={storeLink}
            className={cn(
              "mt-2 inline-flex h-12 items-center justify-center rounded-full text-base font-semibold",
              invert ? "bg-mint text-ink" : "bg-ink text-white",
            )}
          >
            {t("site.common.getApp")}
          </a>
        </nav>
      </div>
    </header>
  );
};
